'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Slim "amber glass" banner shown at the top of a deprecated course /
 * learning path (and their lesson pages). The message is authored in the CMS as
 * rich-text HTML (`depreciation_msg`) and rendered as-is.
 *
 * Theme handling: the fill is a translucent amber that tints whatever surface it
 * sits on (a warm glow on the dark UI, a pale amber on the light UI), while the
 * text/icon use the adaptive `--Text-Default-*` tokens so contrast holds in both
 * themes. `data-swiftype-index="false"` keeps the banner out of Swiftype's index.
 *
 * Stickiness: the page content lives inside an `overflow-y-auto` wrapper, which
 * would trap `position: sticky` in a container that never actually scrolls (the
 * window is the real scroller). So instead of rendering inline, the banner
 * PORTALS into `#deprecation-banner-slot` — a slot in the root layout that sits
 * outside that wrapper, as a sibling of the sticky header. There, `position:
 * sticky` resolves against the window.
 *
 * The nav bar (`[data-academy-navbar]`, the visible one per breakpoint) is itself
 * `sticky top-0 z-50`, so a banner also stuck at `top-0` would hide *behind* it.
 * We measure the visible nav bar's height and offset the banner's `top` by it, so
 * it pins directly beneath the nav. Re-measured on resize (breakpoint swaps
 * desktop/mobile nav, which differ in height).
 */
interface DeprecationBannerProps {
  /** Rich-text HTML from the CMS `depreciation_msg` field. */
  message?: string;
}

function getNavbarHeight(): number {
  const bars = Array.from(
    document.querySelectorAll<HTMLElement>('[data-academy-navbar]'),
  );
  // Use the visible variant for the current breakpoint (the other is display:none).
  const visible = bars.find(
    (bar) => bar.offsetParent !== null || bar.getClientRects().length > 0,
  );
  return visible ? visible.getBoundingClientRect().height : 0;
}

export default function DeprecationBanner({ message }: DeprecationBannerProps) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [navHeight, setNavHeight] = useState(0);

  useEffect(() => {
    setSlot(document.getElementById('deprecation-banner-slot'));

    const measure = () => setNavHeight(getNavbarHeight());
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  if (!message || String(message).trim() === '') return null;
  if (!slot) return null;

  return createPortal(
    <div
      role="alert"
      data-swiftype-index="false"
      className="relative w-full border-y border-[#D4AF37]/40 bg-[#D4AF37]/[0.16] backdrop-blur-[2px] sticky z-40"
      style={{ top: navHeight }}
    >
      {/* warm top sheen so the glass reads as a distinct surface */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-[#F5D97A]/40" />

      <div className="mx-auto flex max-w-6xl items-center justify-center gap-2.5 px-4 py-2.5 leading-none">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#D4AF37]/25 ring-1 ring-[#D4AF37]/45">
          <svg
            className="h-3 w-3 text-[#C99A2E]"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 8v5m0 3h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>

        <span
          className="text-center text-[14px] font-semibold leading-snug tracking-[0.01em] text-[var(--Text-Default-Medium)] [&_a]:font-semibold [&_a]:text-[var(--Text-Default-Strong)] [&_a]:underline [&_a]:decoration-[#C99A2E]/70 [&_a]:decoration-1 [&_a]:underline-offset-[3px] [&_a]:transition-all hover:[&_a]:decoration-[#C99A2E] hover:[&_a]:decoration-2 [&_p]:m-0 [&_p]:inline"
          dangerouslySetInnerHTML={{ __html: message }}
        />
      </div>
    </div>,
    slot,
  );
}
